// 解説ビュー（§12）。数学・前提・限界の常設ページ。計算コアには関与しない静的コンテンツ。
// 各見出しに id を振り、計算ページの注記からアンカーで飛べるようにする。
import { STRENGTH_M } from '../../config';
import { el, mount } from '../dom';

interface Section {
  id: string;
  title: string;
  build: () => HTMLElement;
}

const p = (html: string) => el('p', { html });

const SECTIONS: Section[] = [
  {
    id: 'what',
    title: 'このツールが計算していること',
    build: () =>
      el('div', {}, [
        p('中核は2つ。<b>(A) ケリー基準による推奨軍資金</b>（破産せず複利で増やすのに妥当な手元資金）と、<b>(B) 破産確率カーブ</b>（手元資金ごとに途中で資金が尽きる確率）。'),
        p('設計思想: <b>B（破産シミュ）が現実そのものを表す主役</b>。固定コスト C を毎周払い、ランダムな報酬を受け取る過程をそのまま回しているだけ。<b>A（ケリー）は堅実な軍資金の目安</b>で、フル/ハーフ/クォーターの3点を曲線上に重ねて読むためのもの。既定の推奨は<b>ハーフケリー</b>。'),
      ]),
  },
  {
    id: 'poisson',
    title: '確率モデル: なぜ Poisson か',
    build: () =>
      el('div', {}, [
        p('各ドロップ行の「1周の個数」を <b>Poisson(λ)</b> として扱う（行間は独立と仮定）。'),
        p('ARPG のレアドロップは「大量のモンスター／チェスト等での独立した低確率試行の和」で、これは数学的に Poisson に収束する（law of rare events）。<b>レアで高額な品ほど Poisson 近似は正確</b>になり、ちょうど軍資金を支配するジャックポット項が一番正確になる。'),
        p('「期待個数1」と「確定で1個」は別物（前者は約37%が0個）。確定数で落ちるものは Poisson に入れず、安定収入またはグループドロップで扱う。'),
      ]),
  },
  {
    id: 'rate',
    title: 'ドロップ率 λ の推定',
    build: () =>
      el('div', {}, [
        p('3モードある。<b>直接</b>: λ をそのまま入力。<b>実績</b>: 周回数 N と観測個数 k から推定。素朴な k/N は k=0 で 0 になり「真のレートは正なのに、まだ引けてないだけ」を表せないため、Jeffreys 事前を使った <b>λ̂ = (k+0.5)/N</b>（事後 Gamma(k+0.5, N)）を使う。'),
        p(`<b>実績＋予測</b>: 予測レート λ₀ と信頼度 M（弱め${STRENGTH_M.weak}／ふつう${STRENGTH_M.normal}／強め${STRENGTH_M.strong}＝擬似周回数）を実績 N,k とベイズ合成。事後平均 <b>λ̂ = (λ₀·M + k)/(M + N)</b>。データが薄いうちは λ₀ 寄り、周回が増えると実績寄りへ自動で移る。`),
        p('<b>精度は N ではなく k で決まる</b>: 推定の相対誤差は概ね 1/√k。だから「1000周回した」より「当たりを何個見たか(k)」が効く。k が小さい（既定 k&lt;10）と推定は粗く、レア品は実績が数千周ぶん貯まるまで予測（λ₀）が主役。'),
      ]),
  },
  {
    id: 'uncertainty',
    title: '不確実性の伝播（レートが不確かなことを軍資金に乗せる）',
    build: () =>
      el('div', {}, [
        p('実績／実績＋予測の行は、点推定 λ̂ で固定せず<b>事後 Gamma 分布のままシミュに流す</b>。これにより「ちゃんと測れていないものに賭けるならより慎重に」が定量化され、実績が薄いレア狙いほど推奨軍資金が自動で上がる。引き方は用途で変える:'),
        p('<b>単発の最適露出（ケリー f*）</b>: 標本ごとに λ を引き直してから個数を生成（＝1周の予測分布＝負の二項）。レート不確実性を1周の分散に織り込む。'),
        p('<b>破産シミュ</b>: トラジェクトリごとに λ を<b>1回だけ</b>引いて全周固定。<b>「真のレートが持続的に不運だと溶ける」という系統リスク</b>を表現する。ここが破産リスクに最も効く。周ごとに引き直すと相関が消えて破産リスクを過小評価するため、あえて固定している。'),
      ]),
  },
  {
    id: 'kelly',
    title: 'ケリー軍資金（f* とフル/ハーフ/クォーター）',
    build: () =>
      el('div', {}, [
        p('1周の露出割合を <b>f = C / W</b>（コストが軍資金に占める割合）と定義し、複利成長率 g(f) = E[ ln(1 + f·X/C) ] を最大化する <b>f*</b> をモンテカルロ＋1次元探索で求める。推奨軍資金: フル W=C/f*、ハーフ W=2C/f*、クォーター W=4C/f*。f* は無次元なので表示単位に依存しない。'),
        p('<b>既定がハーフの理由</b>: フルケリーはドロップ率・相場の見積り誤差に過敏で、少し賭けすぎただけで資産が急減する。ハーフは成長をわずかに犠牲にして破産リスクを桁違いに下げるため、見積りが不確実な実ゲームに適する。'),
        p('<b>固定コストと Kelly の関係</b>: ファームは1周のコスト C が資産に関係なく固定。だから W=C/f* は「このコスト C のギャンブルが資産の Kelly 最適割合 f* になる軍資金はいくつか」を逆算した、バンクロール管理の標準的な軍資金サイジングとして読むのが正確（ポーカー／トレードの「必要資金＝ベットサイズ÷Kelly割合」と同じ）。'),
      ]),
  },
  {
    id: 'ruin',
    title: '破産確率カーブ',
    build: () =>
      el('div', {}, [
        p('手元資金 B から始め、毎周 C を払い（払えなければ破産）、ランダム報酬 R を受け取る過程を多数のトラジェクトリで回し、破産した割合を B ごとにプロットする。1周の最大損失はコスト C ちょうど（ドロップは非負）。だから破産は「ハズレ周の連続で資金が C 刻みに削られる」現象。'),
        p('f*・推奨軍資金・破産カーブ・各段階の破産％は、<b>コスト・安定収入・時間・ドロップ行だけに依存し、現在の軍資金 B には依存しない</b>。B は曲線上のマーカーと判定文にだけ使う。'),
        p('注意: 破産％はモンテカルロ推定なので<b>数字に小さなノイズ</b>がある（特に低い破産確率は粗い）。「だいたいこのくらい」として読む。'),
      ]),
  },
  {
    id: 'group',
    title: 'グループドロップ（「確定 m 個」）',
    build: () =>
      el('div', {}, [
        p('ボス報酬テーブル等「グループから確定 m 個、各メンバーの比 w_i」は、1周ごとに <b>ちょうど m 個を比でカテゴリカル抽選</b>して扱う（排他性を正しく表現）。各メンバーの期待個数は λ_i = m·w_i/Σw。'),
        p('<b>期待値も分散も正しい</b>（変動は「どのメンバーが出るか」だけ・個数は確定 m）。とくに <b>最低メンバー価値がコストを上回る確定グループは、負け周ゼロ → 破産 0・軍資金最小</b>になる（毎周必ず m 個落ちるため）。抽選は復元あり（多項）。'),
      ]),
  },
];

// 12.2.8 単純化と限界の表。
interface LimitRow {
  name: string;
  detail: string;
  dir: 'danger' | 'safe' | 'neutral';
  dirText: string;
  effect: string;
}
const LIMITS: LimitRow[] = [
  { name: 'コスト固定 ≠ 割合ベット', detail: 'Kelly は本来「資産の一定割合を賭ける」前提だが、ファームのコスト C は固定', dir: 'neutral', dirText: '—（解釈の問題）', effect: 'W=C/f* は軍資金サイジングの目安として読む。破産カーブを主役にすれば実害なし' },
  { name: 'マップ単位の共通倍率を無視', detail: 'rarity/quantity/pack size/juicing で全ドロップが一斉に上下する相関を独立 Poisson は捉えない', dir: 'danger', dirText: '危険側（実分散が大きい）', effect: '純粋な宝くじ型では二次的。ハーフ既定がほぼ吸収。juicing が激しい構成はクォーター寄りで' },
  { name: 'アイテム価値を固定価格扱い', detail: 'unique/jewel はロールで価値が大きく変わる。manual 行の単一価格はこの価値分散を無視', dir: 'danger', dirText: '危険側（分散を過小評価）', effect: '高額 manual 行があるときは「価値のブレ」が別途あると認識。気になればやや慎重側で' },
  { name: '換金の流動性・時間を無視', detail: 'EV・時給は即座に提示価格で売れる前提。chase 品は売却に時間がかかり下がりうる', dir: 'danger', dirText: '危険側（時給が楽観的）', effect: '時給は上限寄りの目安。換金が重い品は割り引いて解釈' },
  { name: '相場の固定（スナップショット）', detail: '計算時点のレートを固定。リーグ進行で相場は動く', dir: 'neutral', dirText: '状況次第', effect: '適宜「相場を再取得」で更新' },
  { name: '安定収入 S を分散ゼロ扱い', detail: 'S は確定収入として変動を与えない', dir: 'danger', dirText: '入れ方次第で危険側', effect: 'ほぼ毎周確実な低分散の実入りだけ S に。ランダムな当たりは必ずドロップ行へ' },
  { name: 'λ≫1 の Poisson 近似', detail: 'gold 等ほぼ確定で大量に出るものを Poisson 行にすると現実より分散が大きく出る', dir: 'safe', dirText: '安全側だが不正確', effect: 'そういうものは S（安定収入）へ。Poisson 行はランダムな当たり専用' },
  { name: '周回 i.i.d. 前提', detail: '各周は独立と仮定（ストリークや上達の相関は無視）', dir: 'neutral', dirText: '概ね中立', effect: '定常ファームの近似として妥当' },
  { name: '為替は単一 mid レート', detail: '通貨ごとに1レート。実際は bid-ask スプレッドや薄い通貨の流動性がある', dir: 'neutral', dirText: '中立（小さい）', effect: '見立て用途では十分' },
];

function buildLimits(): HTMLElement {
  const body = el('tbody');
  for (const r of LIMITS) {
    body.append(
      el('tr', {}, [
        el('td', {}, [el('b', {}, r.name)]),
        el('td', {}, r.detail),
        el('td', { class: `dir-${r.dir}` }, r.dirText),
        el('td', {}, r.effect),
      ]),
    );
  }
  return el('div', { class: 'tablewrap' }, [
    el('table', { class: 'cmp limits' }, [
      el('thead', {}, [el('tr', {}, [el('th', {}, '前提・単純化'), el('th', {}, '内容'), el('th', {}, '影響の向き'), el('th', {}, '実害と対処')])]),
      body,
    ]),
  ]);
}

function buildTerms(): HTMLElement {
  const dl = el('dl', { class: 'terms' });
  const item = (k: string, v: string) => { dl.append(el('dt', {}, k), el('dd', {}, v)); };
  item('f*（ケリー割合）', 'コストが軍資金に占める最適割合。大きいほど少ない資金で攻めてよい。');
  item('EV/周', '1周あたりの期待利益（プラスでないと、資金管理しても理論上は溶ける）。');
  item('ROI', 'EV ÷ コスト。');
  item('期待時給 / 安定時給(trash)', '当たり込みの期待値 / 当たり抜きの floor 収入。ビルドのクリア速度差はここでしか比較できない。');
  item('90% 信用区間', '実績/実績＋予測モードでの λ の不確かさの幅（事後 Gamma の 5%〜95%）。広いほど「まだよく分かってない」。');
  return dl;
}

export class ExplainView {
  private mounted = false;
  constructor(private readonly root: HTMLElement) {}

  ensureMounted(): void {
    if (this.mounted) return;
    this.mounted = true;
    const sections = SECTIONS.map((s, i) =>
      el('section', { id: `sec-${s.id}`, class: 'prose-sec' }, [
        el('h3', {}, `12.2.${i + 1} ${s.title}`),
        s.build(),
      ]),
    );
    const limits = el('section', { id: 'sec-limits', class: 'prose-sec' }, [
      el('h3', {}, '12.2.8 単純化している前提と既知の限界（必読）'),
      p('「<span class="dir-safe">安全側</span>」＝軍資金を多めに出す＝慎重寄り、「<span class="dir-danger">危険側</span>」＝軍資金を少なめに出しうる＝賭けすぎ寄り。'),
      buildLimits(),
      p('ひとことで言うと: <b>危険側のズレ（共通倍率の相関・価値のブレ・換金ロス）はあるが、いずれもハーフケリーのクッションでおおむね吸収される</b>。だからハーフ既定で使い、破産確率カーブを最終判断の主軸にするのが安全。'),
    ]);
    const terms = el('section', { id: 'sec-terms', class: 'prose-sec' }, [
      el('h3', {}, '12.2.9 数字の読み方・用語'),
      buildTerms(),
    ]);

    mount(
      this.root,
      el('div', { class: 'panel prose' }, [
        el('div', { class: 'panel-head' }, [el('h2', {}, '数学・前提・限界'), el('span', { class: 'num' }, '§12')]),
        p('計算結果の数字を信用して使うために、裏側の数学・意図的に単純化している前提・既知の限界をまとめています。'),
        ...sections,
        limits,
        terms,
      ]),
    );
  }

  scrollTo(anchor: string | undefined): void {
    this.ensureMounted();
    if (!anchor) {
      this.root.scrollIntoView({ block: 'start' });
      return;
    }
    document.getElementById(`sec-${anchor}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}
